/**
 * Formatea el precio de un producto de forma segura.
 */
export function formatPrice(price) {
    if (price === null || price === undefined || price === '' || Number.isNaN(parseFloat(price))) {
        return 'Consultar';
    }
    return `$${Number(price).toLocaleString('es-AR')}`;
}

/**
 * Convierte un texto a "sentence case": baja todo a minúscula y capitaliza la
 * primera letra de cada oración (inicio del texto y lo que sigue a . ! ? …).
 * Reemplaza al CSS `lowercase first-letter:uppercase`, que solo capitalizaba
 * la primera letra del bloque y dejaba en minúscula lo que venía tras un punto.
 * Se usa para las descripciones de producto de la carta (reflejan el campo
 * descripción de Fudo, que llega en mayúsculas/casing inconsistente).
 */
export function toSentenceCase(text) {
    if (!text) return text;
    return text
        .toLocaleLowerCase('es')
        .replace(
            /(^\s*|[.!?…]\s+)(\p{L})/gu,
            (_, lead, letter) => lead + letter.toLocaleUpperCase('es'),
        );
}

/**
 * Moneda es-AR segura para documentos de liquidación (design.md AD3):
 * `formatPrice` devuelve `'Consultar'` para `0`/`null`/`NaN`, correcto para
 * un precio de carta pero catastrófico en un comprobante — un `neto` de `0`
 * es un valor real y frecuente, nunca "a consultar". `null`/`undefined`/
 * `NaN` (genuinamente ausente) siguen devolviendo `'—'`, nunca inventan una
 * palabra.
 * @param {number|null|undefined} n
 * @returns {string}
 */
export function formatMonto(n) {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
    const num = Number(n);
    const signo = num < 0 ? '-' : '';
    const abs = Math.abs(num);
    return `${signo}$${abs.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Fecha SOLO (sin hora) es-AR, zona horaria explícita
 * `America/Argentina/Buenos_Aires` (design.md AD3) — para instantes reales
 * (`creada_at`, `anulada_at`), nunca para columnas Postgres `date` sin
 * componente horario (esas se formatean aparte, ver
 * `liquidacion-comprobante-policy.js#formatFechaPeriodo`, que documenta por
 * qué NO puede reusar esto).
 * @param {string|null|undefined} iso
 * @returns {string}
 */
export function formatFechaCorta(iso) {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('es-AR', {
        timeZone: 'America/Argentina/Buenos_Aires',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
}

/**
 * Fecha+hora es-AR en hora de Argentina, `dd/mm/aaaa HH:mm` con reloj de 24
 * horas (review-ddffaacc59304b7d: antes era una tercera copia local en
 * `liquidacion-comprobante-policy.js`). S7 (acceptance run): el formato
 * por defecto de es-AR salía "26/9, 09:31 a. m." (12 horas, sin año, y
 * dependiente del build de ICU). `hourCycle: 'h23'` fija 00-23 (nunca
 * "24:00" a medianoche, que `hour12: false` puede dar) y el texto se arma
 * con `formatToParts` para no depender de la puntuación de ICU.
 * @param {string|null|undefined} iso
 * @returns {string}
 */
export function formatFechaHora(iso) {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    const partes = Object.fromEntries(
        new Intl.DateTimeFormat('es-AR', {
            timeZone: 'America/Argentina/Buenos_Aires',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        })
            .formatToParts(date)
            .map((p) => [p.type, p.value]),
    );
    return `${partes.day}/${partes.month}/${partes.year} ${partes.hour}:${partes.minute}`;
}

/**
 * `'2026-09'` -> `'septiembre 2026'`: un mes calendario (`YYYY-MM`) en
 * palabras, por slicing del string — nunca `Date` (el mes es una
 * fecha-calendario ART). Algo que no es un mes válido vuelve tal cual (`'—'`
 * si falta).
 * @param {string} mes `'YYYY-MM'`
 * @returns {string}
 */
export function formatMesLargo(mes) {
    const [anio, mesNum] = (mes ?? '').split('-');
    const nombres = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
    ];
    const idx = Number(mesNum) - 1;
    if (!anio || Number.isNaN(idx) || idx < 0 || idx > 11) return mes ?? '—';
    return `${nombres[idx]} ${anio}`;
}

/**
 * Fecha de calendario `YYYY-MM-DD` (una `date` de SQL, ya en hora de
 * Argentina) → `dd/mm/aaaa`, sin pasar por `Date` (que la correría de día
 * según la zona del navegador). Vacío ante un valor inválido.
 * @param {string|null|undefined} ymd
 * @returns {string}
 */
export function formatFechaDia(ymd) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd ?? ''));
    return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}
